import { Admin, Resource, CustomRoutes, useLogin, useLogout, useAuthState } from 'react-admin';
import { Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';

// Providers
import { dataProvider } from './providers/dataProvider';
import { authProvider, getStoredUser, type UserInfo } from './providers/authProvider';
import apolloClient from './providers/apolloClient';

// Theme
import { ekaaTheme } from './theme/ekaaTheme';

// Layout (includes RBACProvider)
import { EkaaLayout } from './layout/EkaaLayout';

// Dashboard
import { Dashboard } from './pages/Dashboard';

// Resources
import { UserList, UserShow } from './resources/users';
import { CardList, CardShow, CardEdit } from './resources/cards';
import { PlanList, PlanDetail, SubscriptionList, SubscriptionDetail, CurrencyList } from './resources/subscriptions';
import {
  PaymentDashboard,
  TransactionList,
  ProviderComparisonPage,
  HybridLimitsEditor,
  CountryRoutingEditor,
  AuditLogViewer,
} from './resources/payments';
import { MidManagement } from './resources/terrain/MidManagement';
import { SectorsPage } from './resources/sectors';
import { SectorSpecialtiesPage } from './resources/sector-specialties';
import { GuardDashboard } from './resources/guard/GuardDashboard';
import { GuardZoneList, GuardZoneCreate, GuardZoneEdit } from './resources/guard/GuardZoneList';
import { GuardImport } from './resources/guard/GuardImport';
import { AuditLogsPage } from './resources/audit';
import { PartnersPage, PartnerDetail } from './resources/partners';
// V1e · Credential Verification moderation queue (LIBERAL)
import { CredentialModerationPage } from './resources/credentials';
import { MerchantCatalogPlanList, MerchantCatalogPlanDetail } from './resources/merchant-catalog-plan';

// Icons
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';

/**
 * useAuth (#1088) — attendu par DashboardLayout.tsx, SettingsPage.tsx,
 * DashboardPage.tsx, LoginPage.tsx (`import { useAuth } from '../App'`),
 * jamais implémenté (tsc TS2614/TS2305). Composé sur les hooks react-admin
 * standard déjà utilisés ailleurs dans ce fichier (AdminAppBar.tsx,
 * AdminMenu.tsx : useGetIdentity/usePermissions) — pas de logique d'auth
 * réinventée, juste exposée sous la forme attendue par ces 4 fichiers.
 *
 * `user` vient de getStoredUser() (authProvider.ts) plutôt que de
 * useGetIdentity() : les consommateurs lisent email/firstName/lastName/
 * roles, une forme plus riche que UserIdentity de react-admin
 * (id/fullName/avatar seulement).
 */
export function useAuth(): {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<unknown>;
  login: () => void;
  loginWithCredentials: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
} {
  const raLogin = useLogin();
  const logout = useLogout();
  const { authenticated, isPending } = useAuthState();

  const loginWithCredentials = async (email: string, password: string) => {
    try {
      await raLogin({ username: email, password });
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erreur de connexion' };
    }
  };

  // SSO Keycloak : jamais implémenté côté authProvider.ts — celui-ci n'a
  // qu'un login(username, password) via proxy API, aucun flux OAuth2/OIDC
  // redirect câblé. Appeler raLogin() sans params planterait immédiatement
  // (authProvider.login détruit {username, password} de undefined).
  // Stub explicite (bouton désactivé côté LoginPage.tsx) plutôt qu'un crash
  // silencieux au clic ou une fausse fonctionnalité inventée ici — cf #1088.
  const login = () => {
    console.warn('[useAuth] Connexion SSO Keycloak non implémentée (#1088).');
  };

  return {
    user: getStoredUser(),
    isAuthenticated: authenticated ?? false,
    isLoading: isPending,
    logout,
    login,
    loginWithCredentials,
  };
}

/**
 * Admin App Content with RBAC
 */
function AdminApp() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      theme={ekaaTheme}
      layout={EkaaLayout}
      dashboard={Dashboard}
      title="EKAA Admin"
      requireAuth
    >
      {/* Users Resource */}
      <Resource
        name="users"
        list={UserList}
        show={UserShow}
        icon={PeopleIcon}
        options={{ label: 'Utilisateurs' }}
      />

      {/* Cards Resource */}
      <Resource
        name="cards"
        list={CardList}
        show={CardShow}
        edit={CardEdit}
        icon={CreditCardIcon}
        options={{ label: 'Cartes' }}
      />

      {/* Plans Resource (Custom Route as list) */}
      <Resource
        name="plans"
        list={PlanList}
        icon={LocalOfferIcon}
        options={{ label: 'Forfaits' }}
      />

      {/* Subscriptions Resource (Custom Route as list) */}
      <Resource
        name="subscriptions"
        list={SubscriptionList}
        icon={SubscriptionsIcon}
        options={{ label: 'Abonnements' }}
      />

      {/* Currencies Resource */}
      <Resource
        name="currencies"
        list={CurrencyList}
        icon={CurrencyExchangeIcon}
        options={{ label: 'Devises' }}
      />

      {/* Merchant Catalog Plans Resource */}
      <Resource
        name="merchant-catalog-plans"
        list={MerchantCatalogPlanList}
        icon={StorefrontIcon}
        options={{ label: 'Catalogues Marchands' }}
      />

      {/* Custom Routes */}
      <CustomRoutes>
        <Route path="/plans/:id" element={<PlanDetail />} />
        <Route path="/subscriptions/:id" element={<SubscriptionDetail />} />
        <Route path="/settings" element={<div>Settings Page</div>} />

        {/* Terrain Routes */}
        <Route path="/terrain" element={<MidManagement />} />

        {/* Payment Routes */}
        <Route path="/payments" element={<PaymentDashboard />} />
        <Route path="/payments/transactions" element={<TransactionList />} />
        <Route path="/payments/providers" element={<ProviderComparisonPage />} />
        <Route path="/payments/hybrid-limits" element={<HybridLimitsEditor />} />
        <Route path="/payments/country-routing" element={<CountryRoutingEditor />} />
        <Route path="/payments/audit-logs" element={<AuditLogViewer />} />

        {/* Sectors Route */}
        <Route path="/sectors" element={<SectorsPage />} />

        {/* Sector Specialties Route */}
        <Route path="/sector-specialties" element={<SectorSpecialtiesPage />} />

        {/* Guard Services Routes */}
        <Route path="/guard" element={<GuardDashboard />} />
        <Route path="/guard-zones" element={<GuardZoneList />} />
        <Route path="/guard-zones/create" element={<GuardZoneCreate />} />
        <Route path="/guard-zones/:id" element={<GuardZoneEdit />} />
        <Route path="/guard-import" element={<GuardImport />} />

        {/* Audit Logs Route */}
        <Route path="/audit" element={<AuditLogsPage />} />

        {/* Partners Routes */}
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/:id" element={<PartnerDetail />} />

        {/* V1e · Credential Verification moderation (queue + Approve/Reject modal) */}
        <Route path="/credentials/moderation" element={<CredentialModerationPage />} />

        {/* Merchant Catalog Plan Routes */}
        <Route path="/merchant-catalog-plans/:id" element={<MerchantCatalogPlanDetail />} />
      </CustomRoutes>
    </Admin>
  );
}

/**
 * EKAA Admin Dashboard
 * Built with React Admin + Custom RBAC
 * Note: RBACProvider is inside EkaaLayout (must be inside Admin context)
 * ApolloProvider wraps everything to enable useQuery in Dashboard
 */
export function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <AdminApp />
    </ApolloProvider>
  );
}

export default App;
